import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Input } from "../../DemoClient/Components/Input";
import { Label } from "../../DemoClient/Components/label";
import { Switch, TOAST } from "retro";
import { ArrowLeft, Save } from "lucide-react";
import { binData, cardTypes, countryReferenceList } from "../../DemoClient/Data/mockData";
import { useParams } from "react-router-dom";

export default function CreateNewBin() {
    const HISTORY = useHistory();
    const [isActive, setIsActive] = useState(false);

    const { id } = useParams();

    useEffect(() => {
        const currentBin = binData.find((b) => b.bin === id);
        if (currentBin) {
            setForm((prev) => ({ ...prev, bin: currentBin.bin }));
            setForm((prev) => ({ ...prev, name: currentBin.name }));
            setIsActive(currentBin.status === "Active");
            setForm((prev) => ({ ...prev, brand: currentBin.brand }));
            setForm((prev) => ({ ...prev, cardType: currentBin.cardType }));
            setForm((prev) => ({ ...prev, cardLevel: currentBin.cardLevel }));
            setForm((prev) => ({ ...prev, fundingType: currentBin.fundingType }));
            setForm((prev) => ({ ...prev, currency: currentBin.currency }));
            setForm((prev) => ({ ...prev, cardNature: currentBin.cardNature }));
            setForm((prev) => ({ ...prev, countryUsage: currentBin.countryUsage }));
            setForm((prev) => ({ ...prev, allowCNP: currentBin.allowCNP }));
            setForm((prev) => ({ ...prev, singleCardUsage: currentBin.singleCardUsage }));
            setForm((prev) => ({ ...prev, cardAdvance: currentBin.cardAdvance }));
        }
    }, [id]);



    // Controlled form state
    const [form, setForm] = useState({
        bin: "",
        name: "",
        brand: "visa",
        cardType: "Corporate Spends",
        cardLevel: "standard",
        fundingType: "credit",
        currency: "aed",
        countryUsage: "uae",
        cardNature: "Virtual",
        allowCNP: false,
        singleCardUsage: false,
        cardAdvance: false,
});

    // Handle input changes
    const handleChange = (e) => {
        const { id, value } = e.target;

        if (id === "countryUsage") {
            // Find the selected country
            const selectedCountry = countryReferenceList.find(c => c.countryName === value);
            setForm({
                ...form,
                countryUsage: value,
                currency: selectedCountry ? selectedCountry.currency : ""
            });
        } else {
            setForm({
                ...form,
                [id]: value
            });
        }
    };

    // Submit handler
    const handleSubmit = () => {

        if (id) {
            TOAST.success("BIN Amendment sent for approval");
            HISTORY.push("/app/demo/bin-management");
            return;
        }

        if (!form.bin || !form.brand || !form.cardType || !form.cardLevel || !form.fundingType || !form.currency || !form.countryUsage || !form.name) {
            TOAST.error("Please fill all the fields");
            return;
        }

        const newBin = {
            id: (binData.length + 1).toString(),
            bin: form.bin,
            name: form.name,
            brand: form.brand === "jaywan" ? "Jaywan" : form.brand === "visa" ? "Visa" : "Mastercard",
            cardType: form.cardType.charAt(0).toUpperCase() + form.cardType.slice(1),
            cardLevel: form.cardLevel.charAt(0).toUpperCase() + form.cardLevel.slice(1),
            fundingType: form.fundingType.charAt(0).toUpperCase() + form.fundingType.slice(1),
            cardNature: form.cardNature.charAt(0).toUpperCase() + form.cardNature.slice(1),
            currency: form.currency === "aed" ? "AED" : "Multi-currency",
            countryUsage:
                form.countryUsage === "uae"
                    ? "UAE Only"
                    : form.countryUsage === "gcc"
                        ? "GCC"
                        : "International",
            status: isActive ? "Active" : "Inactive",
            createdAt: new Date().toISOString().split("T")[0],
            cardAdvance: form.cardAdvance,
        };

        binData.push(newBin);
        TOAST.success("BIN created successfully!");
        HISTORY.push("/app/demo/bin-management");
    };

    // Styles (same as your design)
    const cardStyle = { borderRadius: "0.5rem", border: "1px solid #E5E7EB", padding: "1rem", backgroundColor: "#fff" };
    const inputStyle = { width: "100%", padding: "0.5rem", borderRadius: "0.375rem", border: "1px solid #D1D5DB", fontFamily: "monospace" };
    const selectStyle = { width: "100%", padding: "0.5rem", borderRadius: "0.375rem", border: "1px solid #D1D5DB", backgroundColor: "#F9FAFB" };
    const labelStyle = { display: "block", fontSize: "0.875rem", marginBottom: "0.25rem", fontWeight: 500 };
    const gridStyle2 = { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1.5rem" };
    const flexRowStyle = { display: "flex", alignItems: "center", gap: "0.75rem", paddingTop: "0.5rem" };
    const cardHeaderStyle = { marginBottom: "1rem" };
    const cardTitleStyle = { fontSize: "1.75rem", fontWeight: 600, marginBottom: "0.25rem" };
    const cardDescStyle = { fontSize: "1rem", color: "#6B7280" };
    const buttonStyle = { display: "flex", alignItems: "center", justifyContent: "center", padding: "0.5rem 1rem", borderRadius: "0.375rem", border: "none", backgroundColor: "#2563EB", color: "#fff", cursor: "pointer", fontSize: "0.875rem" };
    const outlineButtonStyle = { display: "flex", alignItems: "center", justifyContent: "center", padding: "0.5rem 1rem", borderRadius: "0.375rem", border: "1px solid #D1D5DB", backgroundColor: "#fff", cursor: "pointer", fontSize: "0.875rem" };

    return (
        <div style={{ maxWidth: "64rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {/* Back button */}


            {/* BIN Configuration */}
            <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                    <h3 style={cardTitleStyle}>BIN Configuration</h3>
                    <p style={cardDescStyle}>Enter the details for the new Bank Identification Number</p>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                    {/* Row 1 */}
                    <div style={gridStyle2}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="bin">BIN Number *</Label>
                            <Input id="bin" value={form.bin} onChange={handleChange} placeholder="Enter 6-8 digit BIN" style={inputStyle} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="name">Card Type Name *</Label>
                            <Input id="name" value={form.name} onChange={handleChange} placeholder="Enter Card Type Name" style={inputStyle} />
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div style={gridStyle2}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="cardType">Card Type *</Label>
                            <select id="cardType" value={form.cardType} onChange={handleChange} style={selectStyle}>
                                {cardTypes.map((cardType) => (
                                    <option key={cardType} value={cardType}>{cardType}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="brand">Card Network *</Label>
                            <select id="brand" value={form.brand} onChange={handleChange} style={selectStyle}>
                                <option value="visa">Visa</option>
                                <option value="mastercard">Mastercard</option>
                                <option value="jaywan">Jaywan</option>
                                <option value="americanExpress">American Express</option>
                            </select>
                        </div>

                    </div>

                    {/* Row 3 */}
                    <div style={gridStyle2}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="fundingType">Funding Type *</Label>
                            <select id="fundingType" value={form.fundingType} onChange={handleChange} style={selectStyle}>
                                <option value="credit">Credit</option>
                                <option value="debit">Debit</option>
                                <option value="prepaid">Prepaid</option>
                            </select>
                        </div>

                        {(form.fundingType.toLowerCase() === "credit") && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                                <Label style={labelStyle}>Enable Card Advance</Label>
                                <div style={flexRowStyle}>
                                    <Switch
                                        state={form.cardAdvance}
                                        onChange={(value) =>
                                            setForm(prev => ({ ...prev, cardAdvance: value }))
                                        }
                                        id="cardAdvance"
                                    />
                                    <Label htmlFor="cardAdvance" style={{ fontWeight: 400 }}>
                                        {form.cardAdvance ? true : false}
                                    </Label>
                                </div>
                            </div>
                        )}

                        </div>
                     

                    {/* Row 4 */}
                    <div style={gridStyle2}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="countryUsage">Country of Usage *</Label>
                            <select
                                id="countryUsage"
                                value={form.countryUsage}
                                onChange={handleChange}
                                style={selectStyle}
                            >
                                <option value="">Select country</option>
                                {countryReferenceList.map((country) => (
                                    <option key={country.countryCode} value={country.countryName}>
                                        {country.countryName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="currency">Currency *</Label>
                            <select
                                id="currency"
                                value={form.currency}
                                onChange={handleChange}
                                style={selectStyle}
                            >
                                <option value="">Select currency</option>
                                {countryReferenceList.map((country) => (
                                    <option key={country.countryCode} value={country.currency}>
                                        {country.currency}
                                    </option>
                                ))}
                                <option value="multi">Multi-currency</option>
                            </select>
                        </div>
                    </div>

                    <div style={gridStyle2}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="cardNature">Card Nature *</Label>
                            <select id="cardNature" value={form.cardNature} onChange={handleChange} style={selectStyle}>
                                <option value="Virtual">Virtual</option>
                                <option value="Physical">Physical</option>
                                <option value="Hybrid">Both (Virtual First then Physical Later)</option>
                            </select>
                        </div>

                        {(form.cardNature === "Virtual" || form.cardNature === "Hybrid") && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                                <Label style={labelStyle}>Enable Single Card Usage</Label>
                                <div style={flexRowStyle}>
                                    <Switch
                                        state={form.singleCardUsage}
                                        onChange={(value) =>
                                            setForm(prev => ({ ...prev, singleCardUsage: value }))
                                        }
                                        id="singleCardUsage"
                                    />
                                    <Label htmlFor="singleCardUsage" style={{ fontWeight: 400 }}>
                                        {form.singleCardUsage ? true : false}
                                    </Label>
                                </div>
                            </div>
                        )}

                    </div>

                    <div style={gridStyle2}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <Label style={labelStyle} htmlFor="cardLevel">Card Level *</Label>
                            <select id="cardLevel" value={form.cardLevel} onChange={handleChange} style={selectStyle}>
                                <option value="standard">Standard</option>
                                <option value="gold">Gold</option>
                                <option value="premium">Premium</option>
                                <option value="platinum">Platinum</option>
                                <option value="elite">Elite</option>
                            </select>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>

                            <Label style={labelStyle}>BIN Status</Label>
                            <div style={flexRowStyle}>
                                <Switch state={isActive} onChange={setIsActive} id="status" />
                                <Label htmlFor="status" style={{ fontWeight: 400 }}>
                                    {isActive ? "Active" : "Inactive"}
                                </Label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                <button style={outlineButtonStyle} onClick={() => HISTORY.push("/app/demo/bin-management")}>
                    Cancel
                </button>
                <button style={buttonStyle} onClick={handleSubmit}>
                    <Save style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />{id ? "Send for Approval" : " Create BIN"}
                </button>
            </div>
        </div>
    );
}
